import {
  faqs,
  howItWorks,
  AnalyticsHeader,
} from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultCheckoutTerms, DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-pride-logo.png";
const Banner =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-pride-banner.jpg";
const Background =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-pride-background.webp";
const seatMap3dPreviewImage =
  "https://ss-leagues.s3.amazonaws.com/MLS/Orlando%20City/orlando-city-seating-chart.jpg";

const config: TeamConfig = {
  teamSchema: "orlandopride_flex",
  browserTitle: "Orlando Pride Flex Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Orlando Pride",

  packs: {
    enabled: true,
    dontOpenPackModal: true,
    disablePackSelectionLossModal: true,
    list: [
      {
        id: 1,
        defaultSelected: true,
        amount: 3,
        title: "3 Game Flex Plan",
        description:
          "Build your custom flex plan by selecting any 3 games and continue to pick your seats for each game.",
        offerMoreMessage:
          "Switch to a 4 Game Flex Plan and get up to 20% off your tickets.",
        buckets: [
          {
            id: 1,
            title: "Upcoming Games",
            description: "",
            max: 3,
            min: 3,
            displayAllEvents: true,
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 0,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 3 more games to continue!",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 2 more games to continue!",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 1 more game to continue!",
          },
          {
            selectedEventCount: 3,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
      {
        id: 2,
        amount: 4,
        title: "4 Game Flex Plan",
        description:
          "Build your custom flex plan by selecting any 4 games and get up to 20% off your tickets.",
        offerMoreMessage: "",
        buckets: [
          {
            id: 1,
            title: "Upcoming Games",
            description: "",
            displayAllEvents: true,
            max: 4,
            min: 4,
          },
        ],
        successMessage:
          "You're good to go! Click Continue to view seating options.",
        updatedAccountGroup: 1,
        customProgressBarLanguage: [
          {
            selectedEventCount: 0,
            progressBarText: "Add 4 more games to continue!",
          },
          {
            selectedEventCount: 1,
            progressBarText: "Add 3 more games to continue!",
          },
          {
            selectedEventCount: 2,
            progressBarText: "Add 2 more games to continue!",
          },
          {
            selectedEventCount: 3,
            progressBarText: "Add 1 more game to continue!",
          },
          {
            selectedEventCount: 4,
            progressBarText:
              "You're good to go! Click Continue to view seating options.",
          },
        ],
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10199-orlandocitysc-sec",
    mapId: "blockmap2d",
    sectionNameMapping: {
      SUPPORTERS: ["GA1", "GA2", "GA3", "GA4", "GA5", "GA6", "GA7"],
    },
    isView3dButtonHiddenBySectionName: [
      "SUPPORTERS",
      "GA1",
      "GA2",
      "GA3",
      "GA4",
      "GA5",
      "GA6",
      "GA7",
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 3,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    defaultSeatCount: 2,
    isPerksEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl:
      "https://flex.attendevents.com/orlandopride_flex/events_home",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/ocsoccer/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage,
    faviconImage: logoImage,
    findGamesBannerImage: Banner,
    authBackgroundImage: Background,
    checkoutStatusPageImage: Background,
    logoImage: `${logoImage}?width=64&height=64`,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,
    },
  },

  colors: {
    primary: "#633492",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: "Thank you for your Orlando Pride purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
